import { apiClient } from './api/client.js';

const ENV_SOURCE = (typeof import.meta !== 'undefined' && import.meta.env)
  ? import.meta.env
  : (typeof process !== 'undefined' ? process.env : {});

const USE_MOCK_DATA = String(ENV_SOURCE?.VITE_ENABLE_MOCK_DATA ?? 'false').toLowerCase() === 'true';
const API_BASE_URL = String(ENV_SOURCE?.VITE_API_URL ?? '').replace(/\/+$/, '');

let mockMembersPromise;
const DEFAULT_LIMIT = 25;
const BULK_DIRECTORY_LIMIT = 100;
const MAX_DIRECTORY_PAGES = 50;

const loadMockMembers = async () => {
  if (!mockMembersPromise) {
    mockMembersPromise = import('./mocks/mockMembers.js');
  }
  return mockMembersPromise;
};

function normalizeStorageUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/') && API_BASE_URL) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
}

function normalizePrivacySettings(value) {
  if (!value) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return normalizePrivacySettings(parsed);
    } catch (error) {
      console.error('[members] failed to parse privacy settings:', error);
      return value;
    }
  }
  if (typeof value !== 'object') return value;
  return {
    showToMembers: value.showToMembers ?? value.show_to_members,
    showToProspects: value.showToProspects ?? value.show_to_prospects,
    receiveEmails: value.receiveEmails ?? value.receive_emails,
    receiveTexts: value.receiveTexts ?? value.receive_texts,
  };
}

function normalizeMember(member) {
  if (!member || typeof member !== 'object') return member;

  const privacySettings = normalizePrivacySettings(
    member.privacySettings ?? member.privacy_settings
  );

  const photo = normalizeStorageUrl(
    member.photo ?? member.profileImage ?? member.profile_image ?? member.avatar
  );
  const businessLogo = normalizeStorageUrl(
    member.businessLogo ?? member.business_logo ?? member.logo
  );

  return {
    ...member,
    id: member.id ?? member.member_id ?? member.memberId ?? member.user_id ?? member.userId,
    name: member.name ?? member.full_name ?? member.fullName,
    photo,
    profileImage: member.profileImage ?? member.profile_image ?? photo,
    businessName: member.businessName ?? member.business_name,
    businessLogo,
    shortDescription: member.shortDescription ?? member.short_description,
    longDescription: member.longDescription ?? member.long_description,
    phone: member.phone ?? member.phone_number ?? member.phoneNumber,
    email: member.email ?? member.email_address ?? member.emailAddress,
    website: member.website ?? member.website_url ?? member.websiteUrl,
    role: member.role ?? member.member_role ?? member.memberRole,
    status: member.status ?? member.member_status ?? member.memberStatus,
    businessAddress: member.businessAddress ?? member.business_address,
    linkedin: member.linkedin ?? member.linkedin_url ?? member.linkedinUrl,
    twitter: member.twitter ?? member.twitter_url ?? member.twitterUrl,
    facebook: member.facebook ?? member.facebook_url ?? member.facebookUrl,
    sixMonthGoals: member.sixMonthGoals ?? member.six_month_goals,
    idealReferral: member.idealReferral ?? member.ideal_referral,
    notificationPreference: member.notificationPreference ?? member.notification_preference,
    photoFocusX: member.photoFocusX ?? member.photo_focus_x ?? null,
    photoFocusY: member.photoFocusY ?? member.photo_focus_y ?? null,
    privacySettings,
  };
}

/**
 * CSS object-position for a member photo, from its focal point.
 * Falls back to top-anchored ("50% 0%") when no focal point is set.
 */
export function photoObjectPosition(member) {
  const x = member?.photoFocusX ?? member?.photo_focus_x;
  const y = member?.photoFocusY ?? member?.photo_focus_y;
  if (x == null || y == null) return '50% 0%';
  return `${x}% ${y}%`;
}

function normalizeMemberList(members) {
  if (!Array.isArray(members)) return [];
  return members.map(normalizeMember);
}

function normalizeToken(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
}

export function classifyDirectoryEntry(entry) {
  const role = normalizeToken(
    entry?.role
    ?? entry?.member_role
    ?? entry?.memberRole
    ?? entry?.backendRole
    ?? entry?.uiRole
  );
  const status = normalizeToken(
    entry?.status
    ?? entry?.member_status
    ?? entry?.memberStatus
    ?? entry?.subscriptionStatus
    ?? entry?.membershipStatus
    ?? entry?.currentStage
    ?? entry?.current_stage
  );
  const prospectStage = normalizeToken(entry?.currentStage ?? entry?.current_stage);
  // Canonical lifecycle kind. Prefer member_type; fall back to legacy role/status heuristics.
  const memberType = normalizeToken(entry?.memberType ?? entry?.member_type);

  const isProspect = Boolean(
    memberType === 'prospect'
    || memberType === 'guest'
    || (!memberType && (
      role === 'prospect'
      || role.includes('prospect')
      || (prospectStage && prospectStage !== 'promoted-to-member')
    ))
  );
  const isSub = !isProspect && Boolean(
    memberType === 'sub'
    || (!memberType && (
      status === 'inactive'
      || status.includes('sub')
      || status.includes('subscriber')
      || status.includes('subscription')
    ))
  );
  const isExecutive = role === 'executive';

  return {
    kind: isProspect ? 'prospect' : (isSub ? 'sub' : 'member'),
    role,
    status,
    isExecutive,
  };
}

export function segmentDirectoryEntries(entries) {
  return (Array.isArray(entries) ? entries : []).reduce(
    (acc, entry) => {
      const classification = classifyDirectoryEntry(entry);
      if (classification.kind === 'prospect') {
        acc.prospects.push(entry);
      } else if (classification.kind === 'sub') {
        acc.subs.push(entry);
      } else {
        acc.members.push(entry);
      }
      return acc;
    },
    { members: [], subs: [], prospects: [] }
  );
}

function buildListResponse(data = {}) {
  const members = normalizeMemberList(data.members ?? []);
  return {
    ...data,
    members,
    hasMore: Boolean(data.hasMore ?? data.has_more),
  };
}

async function getMembersReal(options = {}) {
  const { offset = 0, limit = DEFAULT_LIMIT, role, status, search } = options;
  const params = { offset, limit, role, status, search };

  // Avoid noisy 401s in the browser console by using the public endpoint
  // when there is clearly no auth session available.
  // (We intentionally do NOT call getSession() here because it may itself
  // trigger network requests; this is just a quick local check.)
  try {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage?.getItem('nbna_auth_session_v1')
        ?? window.sessionStorage?.getItem('nbna_auth_session_v1');
      if (!raw) {
        const members = await apiClient.get('/api/public/members', { skipAuth: true });
        return buildListResponse({ members: Array.isArray(members) ? members : [], hasMore: false });
      }

      const parsed = JSON.parse(raw);
      const token = parsed?.accessToken ?? parsed?.token ?? null;
      if (!token) {
        const members = await apiClient.get('/api/public/members', { skipAuth: true });
        return buildListResponse({ members: Array.isArray(members) ? members : [], hasMore: false });
      }
    }
  } catch {
    // Fall back to normal path.
  }

  try {
    const data = await apiClient.get('/api/members', { params });
    return buildListResponse(data ?? {});
  } catch (error) {
    // Public directory fallback (unauthenticated)
    // Backend contract: GET /api/public/members returns an array.
    if (error?.status === 401 || error?.status === 403) {
      const members = await apiClient.get('/api/public/members', { skipAuth: true });
      return buildListResponse({ members: Array.isArray(members) ? members : [], hasMore: false });
    }
    throw error;
  }
}

async function getMemberByIdReal(id) {
  if (id === undefined || id === null) {
    throw new Error('Member id is required');
  }
  const data = await apiClient.get(`/api/members/${id}`);
  return normalizeMember(data);
}

async function searchMembersReal(query, options = {}) {
  const { offset = 0, limit = DEFAULT_LIMIT } = options;
  if (!query) {
    return buildListResponse({ members: [], hasMore: false });
  }
  const params = { q: query, offset, limit };
  const data = await apiClient.get('/api/members/search', { params });
  return buildListResponse(data ?? {});
}

async function createMemberReal(payload) {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Member payload is required');
  }
  const data = await apiClient.post('/api/members', payload);
  return normalizeMember(data);
}

async function updateMemberReal(id, payload) {
  if (id === undefined || id === null) {
    throw new Error('Member id is required');
  }
  if (!payload || typeof payload !== 'object') {
    throw new Error('Member payload is required');
  }
  const data = await apiClient.put(`/api/members/${id}`, payload);
  return normalizeMember(data);
}

async function deleteMemberReal(id) {
  if (id === undefined || id === null) {
    throw new Error('Member id is required');
  }
  await apiClient.delete(`/api/members/${id}`);
  return true;
}

async function updateMemberRoleReal(memberId, role) {
  if (!memberId) {
    throw new Error('Member id is required');
  }
  if (!role) {
    throw new Error('Role is required');
  }
  const data = await apiClient.patch(`/api/members/${memberId}/role`, { role });
  return normalizeMember(data);
}

async function updateMemberStatusReal(memberId, status) {
  if (!memberId) {
    throw new Error('Member id is required');
  }
  if (!status) {
    throw new Error('Status is required');
  }
  const data = await apiClient.patch(`/api/members/${memberId}/status`, { status });
  return normalizeMember(data);
}

async function notifyMemberReal(memberId, method, message) {
  if (!memberId) {
    throw new Error('Member id is required');
  }
  if (!method || !message) {
    throw new Error('Notification method and message are required');
  }
  const data = await apiClient.post(`/api/members/${memberId}/notify`, {
    method,
    message,
  });
  return {
    success: data?.success ?? true,
    message: data?.message ?? 'Notification sent successfully',
  };
}

async function resetMemberPasswordReal(memberId) {
  if (!memberId) {
    throw new Error('Member id is required');
  }
  const data = await apiClient.post(`/api/members/${memberId}/reset-password`);
  return {
    success: data?.success ?? true,
    message: data?.message ?? 'Password reset email sent',
  };
}

async function notImplemented(name) {
  throw new Error(`${name} is not available in mock mode.`);
}

async function collectAllPages(fetchPage, options = {}) {
  const limit = Math.max(1, Number(options.limit ?? BULK_DIRECTORY_LIMIT) || BULK_DIRECTORY_LIMIT);
  let offset = Math.max(0, Number(options.offset ?? 0) || 0);
  let hasMore = false;
  let pageCount = 0;
  const members = [];

  do {
    const response = await fetchPage({ ...options, offset, limit });
    const batch = Array.isArray(response?.members) ? response.members : [];
    members.push(...batch);
    hasMore = Boolean(response?.hasMore);
    offset += batch.length;
    pageCount += 1;

    if (batch.length === 0) {
      hasMore = false;
    }
  } while (hasMore && pageCount < MAX_DIRECTORY_PAGES);

  return {
    members,
    hasMore,
    pagesFetched: pageCount,
  };
}

export const getMembers = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.getMembers(...args);
  }
  return getMembersReal(...args);
};

export const getAllMembers = async (options = {}) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return collectAllPages((pageOptions) => mockMembers.getMembers(pageOptions), options);
  }
  return collectAllPages(getMembersReal, options);
};

export const getMemberById = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.getMemberById(...args);
  }
  return getMemberByIdReal(...args);
};

export const searchMembers = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.searchMembers(...args);
  }
  return searchMembersReal(...args);
};

export const searchAllMembers = async (query, options = {}) => {
  if (!query) {
    return { members: [], hasMore: false, pagesFetched: 0 };
  }
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return collectAllPages((pageOptions) => mockMembers.searchMembers(query, pageOptions), options);
  }
  return collectAllPages((pageOptions) => searchMembersReal(query, pageOptions), options);
};

export const createMember = async (...args) => {
  if (USE_MOCK_DATA) {
    return notImplemented('createMember');
  }
  return createMemberReal(...args);
};

export const updateMember = async (...args) => {
  if (USE_MOCK_DATA) {
    return notImplemented('updateMember');
  }
  return updateMemberReal(...args);
};

export const deleteMember = async (...args) => {
  if (USE_MOCK_DATA) {
    return notImplemented('deleteMember');
  }
  return deleteMemberReal(...args);
};

export const updateMemberRole = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.updateMemberRole(...args);
  }
  return updateMemberRoleReal(...args);
};

export const updateMemberStatus = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.updateMemberStatus(...args);
  }
  return updateMemberStatusReal(...args);
};

export const notifyMember = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.notifyMember(...args);
  }
  return notifyMemberReal(...args);
};

export const resetMemberPassword = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockMembers = await loadMockMembers();
    return mockMembers.resetMemberPassword(...args);
  }
  return resetMemberPasswordReal(...args);
};
